module.exports = {
	"data": {
		"markdownRemark": {
			"html": "<p>In this section, you’re going to implement signup and login functionality that allows users to authenticate against your GraphQL server.</p>\n<h3>Adding a <code>User</code> model</h3>\n<p>The first thing you need is a way to represent user data in the database. To do so, you can add a <code>User</code> type to your Prisma data model.</p>\n<p>You’ll also want to add a <em>relation</em> between the <code>User</code> and the existing <code>Link</code> type to express that <code>Link</code>s are <em>posted</em> by <code>User</code>s.</p>\n<Instruction>\n<p>Open <code>prisma/schema.prisma</code> and add the <code>User</code> model, making sure to also update your existing <code>Link</code> model accordingly:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-graphql\"  path=\".../hackernews-node/prisma/schema.prisma\"  nocopy=\"false\"><code>model Link <span class=\"token punctuation\">{</span>\n  id          Int      <span class=\"token directive function\">@id</span> <span class=\"token directive function\">@default</span><span class=\"token punctuation\">(</span>autoincrement<span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n  createdAt   DateTime <span class=\"token directive function\">@default</span><span class=\"token punctuation\">(</span>now<span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n  description String\n  url         String\n<span class=\"gatsby-highlight-code-line\">  postedBy    User?    <span class=\"token directive function\">@relation</span><span class=\"token punctuation\">(</span><span class=\"token attr-name\">fields</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">[</span>postedById<span class=\"token punctuation\">]</span><span class=\"token punctuation\">,</span> <span class=\"token attr-name\">references</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">[</span>id<span class=\"token punctuation\">]</span><span class=\"token punctuation\">)</span>\n</span><span class=\"gatsby-highlight-code-line\">  postedById  Int?\n</span><span class=\"token punctuation\">}</span>\n\n<span class=\"gatsby-highlight-code-line\">model User <span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">  id        Int      <span class=\"token directive function\">@id</span> <span class=\"token directive function\">@default</span><span class=\"token punctuation\">(</span>autoincrement<span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span>\n</span><span class=\"gatsby-highlight-code-line\">  name      String\n</span><span class=\"gatsby-highlight-code-line\">  email     String   <span class=\"token directive function\">@unique</span>\n</span><span class=\"gatsby-highlight-code-line\">  password  String\n</span><span class=\"gatsby-highlight-code-line\">  links     Link<span class=\"token punctuation\">[</span><span class=\"token punctuation\">]</span>\n</span><span class=\"gatsby-highlight-code-line\"><span class=\"token punctuation\">}</span>\n</span></code></pre>\n      </div>\n</Instruction>\n<p>Here, you can further see how Prisma helps you to reason about your data in a way that is more aligned with how it is represented in the underlying database.</p>\n<h3>Understanding relation fields</h3>\n<p>Notice how you’re adding a new <em>relation field</em> called <code>postedBy</code> to the <code>Link</code> model that points to a <code>User</code> instance. The <code>User</code> model then has a <code>links</code> field that’s a list of\n<code>Link</code>s.</p>\n<p>To do this, we need to also define the relation by annotating the <code>postedBy</code> field with\n<a href=\"https://www.prisma.io/docs/reference/tools-and-interfaces/prisma-schema/relations#the-relation-attribute\">the <code>@relation</code> attribute</a>. This is required for every relation field in\nyour Prisma schema, and all you’re doing is defining what the foreign key of the related table will be. So in this case, we’re adding an extra field to store the <code>id</code> of the <code>User</code>\nwho posts a <code>Link</code>, and then telling Prisma that <code>postedById</code> will be equal to the <code>id</code> field in the <code>User</code> table.</p>\n<p>If this is quite new to you, don’t worry! We’re going to be adding a few of these relational fields and you’ll get the hang of it as you go! For a deeper dive on relations with\nPrisma, check out these <a href=\"https://www.prisma.io/docs/reference/tools-and-interfaces/prisma-schema/relations\">docs</a>.</p>\n<h3>Updating your Prisma Client</h3>\n<p>This is a great time to refresh your memory on the workflow we described for your project at the end of <a href=\"../4-adding-a-database/\">Chapter 4</a>.</p>\n<p>After every change you make to the data model, you need to migrate your database and re-generate Prisma Client. </p>\n<Instruction>\n<p>In the root directory of the project, run the following command:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"  path=\".../hackernews-typescript\"  nocopy=\"false\"><code>npx prisma migrate dev --name <span class=\"token string\">\"add-user-model\"</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>This command has now generated your second migration inside of <code>prisma/migrations</code>, and you can start to see how this becomes a historical record of how your database evolves over\ntime.</p>\n<p>You will notice once again that Prisma Client gets regenerated automatically when you run a migration. Your database structure and Prisma Client has both been updated to reflect the changes for the newly added <code>User</code> model – woohoo! 🎉</p>\n<h3>Extending the GraphQL schema with the <code>User</code> type</h3>\n<p>Now that you have a <code>User</code> data model in your database schema, it’s time to add a <code>User</code> type to your GraphQL schema. To keep your codebase modular and readable, you are going to put the Nexus definitions and resolver code in a separate <code>src/graphql/User.ts</code> file. </p>\n<Instruction>\n<p>Create a <code>User.ts</code> file for the <code>User</code> type:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"  path=\".../hackernews-typescript\"  nocopy=\"false\"><code><span class=\"token function\">touch</span> src/graphql/User.ts\n</code></pre>\n      </div>\n</Instruction>\n<p>Now just like you did with <code>Link</code>, you are going to write the Nexus type definition for the <code>User</code> type using the <code>objectType</code> function. </p>\n<Instruction>\n<p>Type in the following code in <code>src/graphql/User.ts</code> to define the <code>User</code> type:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-typescript\"  path=\"../hackernews-typescript/src/graphql/User.ts\"  nocopy=\"false\"><code><span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span> objectType <span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">\"nexus\"</span><span class=\"token punctuation\">;</span>\n\n<span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> User <span class=\"token operator\">=</span> <span class=\"token function\">objectType</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span>\n    name<span class=\"token punctuation\">:</span> <span class=\"token string\">\"User\"</span><span class=\"token punctuation\">,</span>\n    <span class=\"token function\">definition</span><span class=\"token punctuation\">(</span>t<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n        t<span class=\"token punctuation\">.</span>nonNull<span class=\"token punctuation\">.</span><span class=\"token function\">int</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"id\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n        t<span class=\"token punctuation\">.</span>nonNull<span class=\"token punctuation\">.</span><span class=\"token keyword\">string</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"name\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n        t<span class=\"token punctuation\">.</span>nonNull<span class=\"token punctuation\">.</span><span class=\"token keyword\">string</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"email\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n        t<span class=\"token punctuation\">.</span>nonNull<span class=\"token punctuation\">.</span>list<span class=\"token punctuation\">.</span>nonNull<span class=\"token punctuation\">.</span><span class=\"token function\">field</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"links\"</span><span class=\"token punctuation\">,</span> <span class=\"token punctuation\">{</span>    <span class=\"token comment\" spellcheck=\"true\">// 1</span>\n            type<span class=\"token punctuation\">:</span> <span class=\"token string\">\"Link\"</span><span class=\"token punctuation\">,</span>\n            <span class=\"token function\">resolve</span><span class=\"token punctuation\">(</span>parent<span class=\"token punctuation\">,</span> args<span class=\"token punctuation\">,</span> context<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>   <span class=\"token comment\" spellcheck=\"true\">// 2</span>\n                <span class=\"token keyword\">return</span> context<span class=\"token punctuation\">.</span>prisma<span class=\"token punctuation\">.</span>user  <span class=\"token comment\" spellcheck=\"true\">// 3</span>\n                    <span class=\"token punctuation\">.</span><span class=\"token function\">findUnique</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span> where<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span> id<span class=\"token punctuation\">:</span> parent<span class=\"token punctuation\">.</span>id <span class=\"token punctuation\">}</span> <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span>\n                    <span class=\"token punctuation\">.</span><span class=\"token function\">links</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n            <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n        <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span> \n    <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n<span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</code></pre>\n      </div>\n<p>The field worth discussing is the one called <code>links</code>: </p>\n<ul>\n<li><code>// 1</code>: The <code>links</code> field is a non-nullable array of <code>Link</code> type objects. It represents all the <code>links</code> that have been posted by that particular user.  </li>\n<li><code>// 2</code>: The <code>links</code> field needs to implement a resolve function. Previously, you only needed to implement resolvers for fields in <code>Query</code> and <code>Mutation</code>. Since the resolver for the <code>links</code> field is non-trivial, meaning GraphQL can’t infer it automatically as the <code>User</code> object returned from your database does not automatically contain the <code>links</code> type. So unlike the other fields in the <code>User</code> type, you need to explcitly define the <code>links</code> resolver. </li>\n<li><code>// 3</code>: This is the Prisma query that returns the associated <code>user.links</code> for a certain user from your database. You are using the <code>parent</code> argument which contains all the fields of the user that you are trying to resolve. Using the <code>parent.id</code> you can fetch the appropriate user record from your database and return the relevant <code>links</code> by chaining in the <code>links()</code> call. </li>\n</ul>\n<blockquote>\n<p><strong>Note:</strong> If you’re having a hard time understanding the theory behind the <code>parent</code> argument and non-trivial resolvers, you should review the topic at the end of <a href=\"../2-a-simple-query/\">Chapter 2</a>.</p>\n</blockquote>\n<Instruction>\n<p>Add <code>User.ts</code> to Nexus by exporting it in <code>index.ts</code>:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-typescript\"  path=\"../hackernews-typescript/src/graphql/index.ts\"  nocopy=\"false\"><code><span class=\"token keyword\">export</span> <span class=\"token operator\">*</span> <span class=\"token keyword\">from</span> <span class=\"token string\">\"./Link\"</span><span class=\"token punctuation\">;</span> \n<span class=\"gatsby-highlight-code-line\"><span class=\"token keyword\">export</span> <span class=\"token operator\">*</span> <span class=\"token keyword\">from</span> <span class=\"token string\">\"./User\"</span>\n</span></code></pre>\n      </div>\n</Instruction>\n<p>Now, the <code>makeSchema</code> function in <code>schema.ts</code> will pick up on the new <code>User</code> type automatically. If your server is running in <code>dev</code> mode, your <code>schema.graphql</code> should be immediately updated with the <code>User</code> type. You can also run <code>npm run generate</code> at any time to do this. This is what the type should look like: </p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-graphql\"  path=\"../hackernews-typescript/schema.graphql\"  nocopy=\"true\"><code>type User <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">email</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">id</span><span class=\"token punctuation\">:</span> Int<span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">links</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">[</span>Link<span class=\"token operator\">!</span><span class=\"token punctuation\">]</span><span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">name</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<blockquote>\n<p><strong>Note:</strong> You might have noticed the definition of <code>links</code> field in the GraphQL schema is very similar to the definition of the <code>links</code> field in the <code>schema.prisma</code> file. The Prisma schema language bears a lot of resemblences to the GraphQL SDL, making it easy to reason about the properties of both schema files. </p>\n</blockquote>\n<h3>Updating the <code>Link</code> type</h3>\n<p>Note that, the relation between <code>User</code> and <code>Link</code> is bidirectional. A <code>user</code> has zero or more <code>links</code> that they have created. Similary a single <code>link</code> is <em>optionally</em> connected to a <code>user</code> who <em>posted</em> the link. To reflect this bidirectional relation, you will add a <code>postedBy</code> field to the existing <code>Link</code> model definition in\nyour GraphQL schema. </p>\n<Instruction>\n<p>Update the definition of the <code>Link</code> type in <code>src/graphql/Link.ts</code>:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-typescript\"  path=\"../hackernews-typescript/src/graphql/Link.ts\"  nocopy=\"false\"><code><span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> Link <span class=\"token operator\">=</span> <span class=\"token function\">objectType</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span>\n    name<span class=\"token punctuation\">:</span> <span class=\"token string\">\"Link\"</span><span class=\"token punctuation\">,</span>\n    <span class=\"token function\">definition</span><span class=\"token punctuation\">(</span>t<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n        t<span class=\"token punctuation\">.</span>nonNull<span class=\"token punctuation\">.</span><span class=\"token function\">int</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"id\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n        t<span class=\"token punctuation\">.</span>nonNull<span class=\"token punctuation\">.</span><span class=\"token keyword\">string</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"description\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n        t<span class=\"token punctuation\">.</span>nonNull<span class=\"token punctuation\">.</span><span class=\"token keyword\">string</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"url\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n<span class=\"gatsby-highlight-code-line\">        t<span class=\"token punctuation\">.</span><span class=\"token function\">field</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"postedBy\"</span><span class=\"token punctuation\">,</span> <span class=\"token punctuation\">{</span>   <span class=\"token comment\" spellcheck=\"true\">// 1</span>\n</span><span class=\"gatsby-highlight-code-line\">            type<span class=\"token punctuation\">:</span> <span class=\"token string\">\"User\"</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">            <span class=\"token function\">resolve</span><span class=\"token punctuation\">(</span>parent<span class=\"token punctuation\">,</span> args<span class=\"token punctuation\">,</span> context<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>  <span class=\"token comment\" spellcheck=\"true\">// 2</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">return</span> context<span class=\"token punctuation\">.</span>prisma<span class=\"token punctuation\">.</span>link\n</span><span class=\"gatsby-highlight-code-line\">                    <span class=\"token punctuation\">.</span><span class=\"token function\">findUnique</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span> where<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span> id<span class=\"token punctuation\">:</span> parent<span class=\"token punctuation\">.</span>id <span class=\"token punctuation\">}</span> <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span>\n</span><span class=\"gatsby-highlight-code-line\">                    <span class=\"token punctuation\">.</span><span class=\"token function\">postedBy</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">            <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">        <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span>    <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n<span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</code></pre>\n      </div>\n<p>What are the changes here? Let’s take a look 🔎:</p>\n<ul>\n<li>\n<p><code>// 1</code>: You are adding a <code>postedBy</code> field of type <code>User</code>. Notice this field does not have a <code>nonNull</code> attached, meaning it is an optional field (it can return <code>null</code>). </p>\n</li>\n<li>\n<p><code>// 2</code>: The implementaiton of the resolver for <code>postedBy</code> should feel familiar, as it is very similar to what you did for the <code>links</code> field in <code>User</code>. In the query, you are fetching the <code>link</code> record first using <code>findUnique({ where: { id: parent.id } })</code> and then the associated <code>user</code> relation who posted the link by chaining <code>postedBy()</code>.  </p>\n</li>\n</ul>\n<blockquote>\n<p><strong>Note:</strong> This interesting syntax where you can traverse and fetch relation fields by chaining the field name (<code>findUnique(...).relationFieldName()</code>) is called the <a href=\"https://www.prisma.io/docs/concepts/components/prisma-client/relation-queries#fluent-api\">Fluent API</a> in Prisma. This API has a very interesting batching behavior that is very useful for implementing GraphQL resolvers as it solves a common problem known as the <em>“N+1 problem”</em>. This is an advanced optimization topic that we won’t cover in this tutorial, but feel free to learn more from <a href=\"https://www.prisma.io/docs/guides/performance-and-optimization/query-optimization-performance#solving-n1-in-graphql-with-findunique-and-prismas-dataloader\">here</a>. </p>\n</blockquote>\n<p>The change in your GraphQL schema is pretty minimal. Let’s take a look:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-graphql\"  path=\"../hackernews-typescript/schema.graphql\"  nocopy=\"true\"><code>type Link <span class=\"token punctuation\">{</span>\n  <span class=\"token attr-name\">description</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">id</span><span class=\"token punctuation\">:</span> Int<span class=\"token operator\">!</span>\n<span class=\"gatsby-highlight-code-line\">  <span class=\"token attr-name\">postedBy</span><span class=\"token punctuation\">:</span> User\n</span>  <span class=\"token attr-name\">url</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n<p>Now that you have defined the <code>User</code> type and updated the <code>Link</code> type in your GraphQL schema and database models, you’re all set to implement authentication! </p>\n<h3>Implementing the <code>signup</code> and <code>login</code> mutation</h3>\n<p>You will be implementing <a href=\"https://morioh.com/p/79f6f8b073f8\">JWT-based authentication</a> for the API. To do this, you will need to add two new fields to the <code>Mutation</code> type, one for <code>login</code> and another for <code>signup</code>. This is what the final GraphQL API will look like for these two fields:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-graphql\"   nocopy=\"true\"><code>type AuthPayload <span class=\"token punctuation\">{</span>  // <span class=\"token number\">1</span>\n  <span class=\"token attr-name\">token</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span>\n  <span class=\"token attr-name\">user</span><span class=\"token punctuation\">:</span> User<span class=\"token operator\">!</span>\n<span class=\"token punctuation\">}</span>\n\n\ntype Mutation <span class=\"token punctuation\">{</span>\n  signup<span class=\"token punctuation\">(</span><span class=\"token attr-name\">email</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span><span class=\"token punctuation\">,</span> <span class=\"token attr-name\">password</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span><span class=\"token punctuation\">,</span> <span class=\"token attr-name\">name</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">:</span> AuthPayload   // <span class=\"token number\">2</span>\n  login<span class=\"token punctuation\">(</span><span class=\"token attr-name\">email</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span><span class=\"token punctuation\">,</span> <span class=\"token attr-name\">password</span><span class=\"token punctuation\">:</span> String<span class=\"token operator\">!</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">:</span> AuthPayload   // <span class=\"token number\">3</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n<ul>\n<li><code>// 1</code>: The <code>AuthPayload</code> is a new type and it is very important to our JWT-based authentication scheme. It is the return type for both <code>signup</code> and <code>login</code> and contains a JWT token in the <code>token</code> field. The <code>user</code> field returns data for the <code>user</code> which initiated the <code>signup</code> or <code>login</code>. </li>\n<li><code>// 2</code>: The <code>signup</code> mutation will create a new <code>user</code> record in the database as well as generate a <code>token</code> that they can use later. The arguments are <code>email</code>, <code>password</code> and <code>name</code>, matching those needed to create a <code>user</code> record. </li>\n<li><code>// 3</code>: The <code>login</code> mutation will check the provided <code>email</code> and <code>password</code> and login an existing user. </li>\n</ul>\n<blockquote>\n<p><strong>Note:</strong> We did something different this time from the typical workflow we have been following throughout this tutorial. We designed the GraphQL fields and types in SDL <em>before</em> writing the Nexus code for them. For many people, designing the schema outline in SDL <em>beforehand</em> is more intuitive. So feel free to do write the SDL beforehand if you prefer. You can always convert your SDL into valid Nexus types using the <a href=\"https://nexusjs.org/converter\">Nexus SDL converter</a>.</p>\n</blockquote>\n<p>First, you will need a new file for the auth code. You will also need to install a few dependencies. <code>bcryptjs</code> is a cryptography library that helps you hash and securely store a password. <code>jsonwebtoken</code> is a library to generate and verify JSON Web Tokens.</p>\n<Instruction>\n<p>Create a new <code>Auth.ts</code> file and install the two libraries (along with their types): </p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"  path=\".../hackernews-typescript/\"  nocopy=\"false\"><code><span class=\"token function\">touch</span> src/graphql/Auth.ts\n<span class=\"token function\">npm</span> <span class=\"token function\">install</span> bcryptjs@~2.4.0 jsonwebtoken@~8.5.0  \n<span class=\"token function\">npm</span> <span class=\"token function\">install</span> --save-dev @types/bcryptjs@~2.4.0  @types/jsonwebtoken@~8.5.0\n</code></pre>\n      </div>\n</Instruction>\n<p>Now you will define the types related to authentication using Nexus. </p>\n<Instruction>\n<p>Define the <code>AuthPayLoad</code> type in <code>Auth.ts</code>: </p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-typescript\"  path=\"../hackernews-typescript/src/graphql/Auth.ts\"  nocopy=\"false\"><code><span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span> objectType <span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">\"nexus\"</span><span class=\"token punctuation\">;</span>\n\n<span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> AuthPayload <span class=\"token operator\">=</span> <span class=\"token function\">objectType</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span>\n    name<span class=\"token punctuation\">:</span> <span class=\"token string\">\"AuthPayload\"</span><span class=\"token punctuation\">,</span>\n    <span class=\"token function\">definition</span><span class=\"token punctuation\">(</span>t<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n        t<span class=\"token punctuation\">.</span>nonNull<span class=\"token punctuation\">.</span><span class=\"token keyword\">string</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"token\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n        t<span class=\"token punctuation\">.</span>nonNull<span class=\"token punctuation\">.</span><span class=\"token function\">field</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"user\"</span><span class=\"token punctuation\">,</span> <span class=\"token punctuation\">{</span>\n            type<span class=\"token punctuation\">:</span> <span class=\"token string\">\"User\"</span><span class=\"token punctuation\">,</span>\n        <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n    <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n<span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>You should be sufficiently familiar by now with the Nexus syntax to understand what’s going on here. Now it’s time to implement the <code>signup</code> mutation. </p>\n<Instruction>\n<p>Add the <code>signup</code> mutation and resolver in <code>Auth.ts</code> using <code>extendType</code> from Nexus:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-typescript\"  path=\"../hackernews-typescript/src/graphql/Auth.ts\"  nocopy=\"false\"><code><span class=\"gatsby-highlight-code-line\"><span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span> objectType<span class=\"token punctuation\">,</span> extendType<span class=\"token punctuation\">,</span> nonNull<span class=\"token punctuation\">,</span> stringArg <span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">\"nexus\"</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\"><span class=\"token keyword\">import</span> <span class=\"token operator\">*</span> as bcrypt <span class=\"token keyword\">from</span> <span class=\"token string\">\"bcryptjs\"</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\"><span class=\"token keyword\">import</span> <span class=\"token operator\">*</span> as jwt <span class=\"token keyword\">from</span> <span class=\"token string\">\"jsonwebtoken\"</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\"><span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span> APP_SECRET <span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">\"../utils/auth\"</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">\n</span><span class=\"gatsby-highlight-code-line\">\n</span><span class=\"gatsby-highlight-code-line\"><span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> AuthMutation <span class=\"token operator\">=</span> <span class=\"token function\">extendType</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">    type<span class=\"token punctuation\">:</span> <span class=\"token string\">\"Mutation\"</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">    <span class=\"token function\">definition</span><span class=\"token punctuation\">(</span>t<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">        t<span class=\"token punctuation\">.</span>nonNull<span class=\"token punctuation\">.</span><span class=\"token function\">field</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"signup\"</span><span class=\"token punctuation\">,</span> <span class=\"token punctuation\">{</span> <span class=\"token comment\" spellcheck=\"true\">// 1</span>\n</span><span class=\"gatsby-highlight-code-line\">            type<span class=\"token punctuation\">:</span> <span class=\"token string\">\"AuthPayload\"</span><span class=\"token punctuation\">,</span>  \n</span><span class=\"gatsby-highlight-code-line\">            args<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span>  \n</span><span class=\"gatsby-highlight-code-line\">                email<span class=\"token punctuation\">:</span> <span class=\"token function\">nonNull</span><span class=\"token punctuation\">(</span><span class=\"token function\">stringArg</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span> \n</span><span class=\"gatsby-highlight-code-line\">                password<span class=\"token punctuation\">:</span> <span class=\"token function\">nonNull</span><span class=\"token punctuation\">(</span><span class=\"token function\">stringArg</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">                name<span class=\"token punctuation\">:</span> <span class=\"token function\">nonNull</span><span class=\"token punctuation\">(</span><span class=\"token function\">stringArg</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">            <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">            async <span class=\"token function\">resolve</span><span class=\"token punctuation\">(</span>parent<span class=\"token punctuation\">,</span> args<span class=\"token punctuation\">,</span> context<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">const</span> <span class=\"token punctuation\">{</span> email<span class=\"token punctuation\">,</span> name <span class=\"token punctuation\">}</span> <span class=\"token operator\">=</span> args<span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token comment\" spellcheck=\"true\">// 2</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">const</span> password <span class=\"token operator\">=</span> await bcrypt<span class=\"token punctuation\">.</span><span class=\"token function\">hash</span><span class=\"token punctuation\">(</span>args<span class=\"token punctuation\">.</span>password<span class=\"token punctuation\">,</span> <span class=\"token number\">10</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token comment\" spellcheck=\"true\">// 3</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">const</span> user <span class=\"token operator\">=</span> await context<span class=\"token punctuation\">.</span>prisma<span class=\"token punctuation\">.</span>user<span class=\"token punctuation\">.</span><span class=\"token function\">create</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">                    data<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span> email<span class=\"token punctuation\">,</span> name<span class=\"token punctuation\">,</span> password <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token comment\" spellcheck=\"true\">// 4</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">const</span> token <span class=\"token operator\">=</span> jwt<span class=\"token punctuation\">.</span><span class=\"token function\">sign</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span> userId<span class=\"token punctuation\">:</span> user<span class=\"token punctuation\">.</span>id <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span> APP_SECRET<span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token comment\" spellcheck=\"true\">// 5</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">return</span> <span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">                    token<span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">                    user<span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token punctuation\">}</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">            <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">        <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">    <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\"><span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span></code></pre>\n      </div>\n</Instruction>\n<p>Let’s use the good ol’ numbered comments again to understand what’s going on here: </p>\n<ul>\n<li><code>// 1</code>: The <code>signup</code> mutation field returns an instace of <code>AuthPayLoad</code>. It has three arguments,  <code>email</code>, <code>password</code> and <code>name</code>, all of which are mandatory. This is the exact same method signature you saw before for the <code>signup</code> mutation. </li>\n<li><code>// 2</code>: In the <code>signup</code> mutation resolver, the first thing to do is hash the <code>User</code>’s password using the <code>bcryptjs</code> library.</li>\n<li><code>// 3</code>: The next step is to use your Prisma Client instance to store the new <code>User</code> record in the database.</li>\n<li><code>// 4</code>: You’re then generating a JSON Web Token which is signed with an <code>APP_SECRET</code>. The information encoded in the token is the <code>id</code> of the newly created user. You still need to create and export this <code>APP_SECRET</code>, something we will cover soon.</li>\n<li><code>// 5</code>: Finally, you return the <code>token</code> and the <code>user</code> in an object that adheres to the shape of the <code>AuthPayload</code> type that you just defined.</li>\n</ul>\n<p>You’ll notice that your IDE raises a few errors right now, like the <code>APP_SECRET</code> import at the top. But these will get fixed soon. Now continue by adding the <code>login</code> mutation. To do this you don’t need to call <code>extendType</code> again, you just add a <code>login</code> field alongside the existing <code>signup</code> field in the <code>definition(t)</code> call inside <code>AuthMutation</code>.</p>\n<Instruction>\n<p>In <code>Auth.ts</code>, extend the definition of <code>mutation</code> to add a new <code>login</code> field: </p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-typescript\"  path=\"../hackernews-typescript/src/graphql/Auth.ts\"  nocopy=\"false\"><code><span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> AuthMutation <span class=\"token operator\">=</span> <span class=\"token function\">extendType</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span>\n    type<span class=\"token punctuation\">:</span> <span class=\"token string\">\"Mutation\"</span><span class=\"token punctuation\">,</span>\n    <span class=\"token function\">definition</span><span class=\"token punctuation\">(</span>t<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n        \n<span class=\"gatsby-highlight-code-line\">        t<span class=\"token punctuation\">.</span>nonNull<span class=\"token punctuation\">.</span><span class=\"token function\">field</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"login\"</span><span class=\"token punctuation\">,</span> <span class=\"token punctuation\">{</span> \n</span><span class=\"gatsby-highlight-code-line\">            type<span class=\"token punctuation\">:</span> <span class=\"token string\">\"AuthPayload\"</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">            args<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">                email<span class=\"token punctuation\">:</span> <span class=\"token function\">nonNull</span><span class=\"token punctuation\">(</span><span class=\"token function\">stringArg</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">                password<span class=\"token punctuation\">:</span> <span class=\"token function\">nonNull</span><span class=\"token punctuation\">(</span><span class=\"token function\">stringArg</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">            <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">            async <span class=\"token function\">resolve</span><span class=\"token punctuation\">(</span>parent<span class=\"token punctuation\">,</span> args<span class=\"token punctuation\">,</span> context<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token comment\" spellcheck=\"true\">// 1</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">const</span> user <span class=\"token operator\">=</span> await context<span class=\"token punctuation\">.</span>prisma<span class=\"token punctuation\">.</span>user<span class=\"token punctuation\">.</span><span class=\"token function\">findUnique</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">                    where<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span> email<span class=\"token punctuation\">:</span> args<span class=\"token punctuation\">.</span>email <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">if</span> <span class=\"token punctuation\">(</span><span class=\"token operator\">!</span>user<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">                    <span class=\"token keyword\">throw</span> <span class=\"token keyword\">new</span> <span class=\"token class-name\">Error</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"No such user found\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token punctuation\">}</span>\n</span><span class=\"gatsby-highlight-code-line\">\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token comment\" spellcheck=\"true\">// 2</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">const</span> valid <span class=\"token operator\">=</span> await bcrypt<span class=\"token punctuation\">.</span><span class=\"token function\">compare</span><span class=\"token punctuation\">(</span>\n</span><span class=\"gatsby-highlight-code-line\">                    args<span class=\"token punctuation\">.</span>password<span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">                    user<span class=\"token punctuation\">.</span>password<span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">if</span> <span class=\"token punctuation\">(</span><span class=\"token operator\">!</span>valid<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">                    <span class=\"token keyword\">throw</span> <span class=\"token keyword\">new</span> <span class=\"token class-name\">Error</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"Invalid password\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token punctuation\">}</span>\n</span><span class=\"gatsby-highlight-code-line\">\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token comment\" spellcheck=\"true\">// 3</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">const</span> token <span class=\"token operator\">=</span> jwt<span class=\"token punctuation\">.</span><span class=\"token function\">sign</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span> userId<span class=\"token punctuation\">:</span> user<span class=\"token punctuation\">.</span>id <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span> APP_SECRET<span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token comment\" spellcheck=\"true\">// 4</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">return</span> <span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">                    token<span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">                    user<span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token punctuation\">}</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">            <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">        <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span>\n        t<span class=\"token punctuation\">.</span>nonNull<span class=\"token punctuation\">.</span><span class=\"token function\">field</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"signup\"</span><span class=\"token punctuation\">,</span> <span class=\"token punctuation\">{</span>\n            type<span class=\"token punctuation\">:</span> <span class=\"token string\">\"AuthPayload\"</span><span class=\"token punctuation\">,</span>\n            args<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span>\n                email<span class=\"token punctuation\">:</span> <span class=\"token function\">nonNull</span><span class=\"token punctuation\">(</span><span class=\"token function\">stringArg</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n                password<span class=\"token punctuation\">:</span> <span class=\"token function\">nonNull</span><span class=\"token punctuation\">(</span><span class=\"token function\">stringArg</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n                name<span class=\"token punctuation\">:</span> <span class=\"token function\">nonNull</span><span class=\"token punctuation\">(</span><span class=\"token function\">stringArg</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n            <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n            async <span class=\"token function\">resolve</span><span class=\"token punctuation\">(</span>parent<span class=\"token punctuation\">,</span> args<span class=\"token punctuation\">,</span> context<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n                <span class=\"token keyword\">const</span> <span class=\"token punctuation\">{</span> email<span class=\"token punctuation\">,</span> name <span class=\"token punctuation\">}</span> <span class=\"token operator\">=</span> args<span class=\"token punctuation\">;</span>\n\n                <span class=\"token keyword\">const</span> password <span class=\"token operator\">=</span> await bcrypt<span class=\"token punctuation\">.</span><span class=\"token function\">hash</span><span class=\"token punctuation\">(</span>args<span class=\"token punctuation\">.</span>password<span class=\"token punctuation\">,</span> <span class=\"token number\">10</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n\n                <span class=\"token keyword\">const</span> user <span class=\"token operator\">=</span> await context<span class=\"token punctuation\">.</span>prisma<span class=\"token punctuation\">.</span>user<span class=\"token punctuation\">.</span><span class=\"token function\">create</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span>\n                    data<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span> email<span class=\"token punctuation\">,</span> name<span class=\"token punctuation\">,</span> password <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n                <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n\n                <span class=\"token keyword\">const</span> token <span class=\"token operator\">=</span> jwt<span class=\"token punctuation\">.</span><span class=\"token function\">sign</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span> userId<span class=\"token punctuation\">:</span> user<span class=\"token punctuation\">.</span>id <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span> APP_SECRET<span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n\n                <span class=\"token keyword\">return</span> <span class=\"token punctuation\">{</span>\n                    token<span class=\"token punctuation\">,</span>\n                    user<span class=\"token punctuation\">,</span>\n                <span class=\"token punctuation\">}</span><span class=\"token punctuation\">;</span>\n            <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n        <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n    <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n<span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>You will notice that the signature for the <code>login</code> field is quite similar to that of <code>signup</code>. Let’s go through the numbered comments: </p>\n<ul>\n<li><code>// 1</code>: Instead of <em>creating</em> a new <code>User</code> object, you’re now using your Prisma Client instance to retrieve an existing <code>User</code> record by the <code>email</code> address that was sent along as an\nargument in the <code>login</code> mutation. If no <code>User</code> with that email address was found, you’re returning a corresponding error.</li>\n<li><code>// 2</code>: The next step is to compare the provided password with the one that is stored in the database. If the two don’t match, you’re returning an error as well.</li>\n<li><code>// 3</code>: You are creating a JWT token, just like <code>signup</code>. The information encoded in the token you are issuing contains the <code>id</code> of the user trying to log in.  </li>\n<li><code>// 4</code>: In the end, you’re returning <code>token</code> and <code>user</code> again.</li>\n</ul>\n<blockquote>\n<p><strong>Note:</strong> Notice that resolvers for both <code>login</code> and <code>signup</code> are <code>async</code>. This is because you need to perform a few asynchronous operations and make use of the <code>await</code> keyword in these resolvers. </p>\n</blockquote>\n<p>Before these new mutations can work, you need to do a few small things. First add the <code>graphql/Auth.ts</code> exports to <code>index.ts</code>.</p>\n<Instruction>\n<p>Add the <code>graphql/Auth.ts</code> exports to <code>index.ts</code> so Nexus can generate the new mutations:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-typescript\"  path=\"../hackernews-typescript/src/graphql/index.ts\"  nocopy=\"false\"><code><span class=\"token keyword\">export</span> <span class=\"token operator\">*</span> <span class=\"token keyword\">from</span> <span class=\"token string\">\"./Link\"</span><span class=\"token punctuation\">;</span>\n<span class=\"token keyword\">export</span> <span class=\"token operator\">*</span> <span class=\"token keyword\">from</span> <span class=\"token string\">\"./User\"</span><span class=\"token punctuation\">;</span>\n<span class=\"gatsby-highlight-code-line\"><span class=\"token keyword\">export</span> <span class=\"token operator\">*</span> <span class=\"token keyword\">from</span> <span class=\"token string\">\"./Auth\"</span><span class=\"token punctuation\">;</span>\n</span></code></pre>\n      </div>\n</Instruction>\n<p>Now create the <code>src/utils/auth.ts</code> file and export the <code>APP_SECRET</code> constant. </p>\n<Instruction>\n<p>Create a <code>src/utils/auth.ts</code> file to store some utilities needed for authentication (and authorization):</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"  path=\".../hackernews-node\"  nocopy=\"false\"><code><span class=\"token function\">mkdir</span> src/utils\n<span class=\"token function\">touch</span> src/utils/auth.ts\n</code></pre>\n      </div>\n</Instruction>\n<Instruction>\n<p>Export the <code>APP_SECRET</code> in the file you just created:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-typescript\"  path=\".../hackernews-typescript/src/utils/auth.ts\"  nocopy=\"false\"><code><span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> APP_SECRET <span class=\"token operator\">=</span> <span class=\"token string\">\"GraphQL-is-aw3some\"</span><span class=\"token punctuation\">;</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>This <code>APP_SECRET</code> is well…a secret! It’s used to sign and verify the authenticity of the JWTs generated by your server. Its value can really be anything as long as it is sufficiently long and random so that an attacker can’t easily guess what it is. </p>\n<blockquote>\n<p><strong>Note:</strong> It’s very important that you protect the value of <code>APP_SECRET</code>. If any third party acquires this value they can trivially forge JWTs and break your authentication scheme. Secrets like this should <em>never</em> be kept inside the code itself in a production application as you risk leaking it with version control. You could use something like an <code>.env</code> file to securely store the value of <code>APP_SECRET</code>. However, since this is a tutorial, we decided to keep things simple and not take these measures. </p>\n</blockquote>\n<p>Whew, the <code>login</code> and <code>signup</code> mutation is finally complete 🎉. You should see the new mutation updated in your <code>schema.graphql</code> file. </p>\n<h3>Verifying client side JWT tokens</h3>\n<p>In the last section you implemented the <code>login</code> and <code>signup</code> mutation, both of which return a valid JWT token of the user (along with data about the user) as a <code>AuthPayLoad</code> type object. However, there’s one important piece of the puzzle still missing: allowing the server verify a request with a valid JWT token attached to it.</p>\n<p>In order to do this, we are going to use the <a href=\"https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Authorization\">HTTP Authorization header</a>. Essentially, any API request from a client with a valid JWT token will include a header called <code>Authorization</code> which will contain said token. The server will then verify the JWT token before proceeding with the API request. In case of an invalid token, the server will return an error. </p>\n<Instruction>\n<p>Write the following utility function in <code>src/utils/auth.ts</code> to decode a JWT token:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-typescript\"  path=\".../hackernews-typescript/src/utils/auth.ts\"  nocopy=\"false\"><code><span class=\"gatsby-highlight-code-line\"><span class=\"token keyword\">import</span> <span class=\"token operator\">*</span> as jwt <span class=\"token keyword\">from</span> <span class=\"token string\">\"jsonwebtoken\"</span><span class=\"token punctuation\">;</span>\n</span>\n<span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> APP_SECRET <span class=\"token operator\">=</span> <span class=\"token string\">\"GraphQL-is-aw3some\"</span><span class=\"token punctuation\">;</span>\n\n<span class=\"gatsby-highlight-code-line\"><span class=\"token keyword\">export</span> <span class=\"token keyword\">interface</span> <span class=\"token class-name\">AuthTokenPayload</span> <span class=\"token punctuation\">{</span>  <span class=\"token comment\" spellcheck=\"true\">// 1</span>\n</span><span class=\"gatsby-highlight-code-line\">    userId<span class=\"token punctuation\">:</span> <span class=\"token keyword\">number</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\"><span class=\"token punctuation\">}</span>\n</span><span class=\"gatsby-highlight-code-line\">\n</span><span class=\"gatsby-highlight-code-line\"><span class=\"token keyword\">export</span> <span class=\"token keyword\">function</span> <span class=\"token function\">decodeAuthHeader</span><span class=\"token punctuation\">(</span>authHeader<span class=\"token punctuation\">:</span> String<span class=\"token punctuation\">)</span><span class=\"token punctuation\">:</span> AuthTokenPayload <span class=\"token punctuation\">{</span> <span class=\"token comment\" spellcheck=\"true\">// 2</span>\n</span><span class=\"gatsby-highlight-code-line\">    <span class=\"token keyword\">const</span> token <span class=\"token operator\">=</span> authHeader<span class=\"token punctuation\">.</span><span class=\"token function\">replace</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"Bearer \"</span><span class=\"token punctuation\">,</span> <span class=\"token string\">\"\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>  <span class=\"token comment\" spellcheck=\"true\">// 3</span>\n</span><span class=\"gatsby-highlight-code-line\">\n</span><span class=\"gatsby-highlight-code-line\">    <span class=\"token keyword\">if</span> <span class=\"token punctuation\">(</span><span class=\"token operator\">!</span>token<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">        <span class=\"token keyword\">throw</span> <span class=\"token keyword\">new</span> <span class=\"token class-name\">Error</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"No token found\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">    <span class=\"token punctuation\">}</span>\n</span><span class=\"gatsby-highlight-code-line\">    <span class=\"token keyword\">return</span> jwt<span class=\"token punctuation\">.</span><span class=\"token function\">verify</span><span class=\"token punctuation\">(</span>token<span class=\"token punctuation\">,</span> APP_SECRET<span class=\"token punctuation\">)</span> as AuthTokenPayload<span class=\"token punctuation\">;</span>  <span class=\"token comment\" spellcheck=\"true\">// 4</span>\n</span><span class=\"gatsby-highlight-code-line\"><span class=\"token punctuation\">}</span>\n</span></code></pre>\n      </div>\n</Instruction>\n<p>Let’s understand what is going on here: </p>\n<ul>\n<li><code>// 1</code>: <code>AuthTokenPayload</code> interface is based on the shape of the JWT token that we issued during <code>signup</code> and <code>login</code>. When the server decodes an issued token, it should expect a response in this format. </li>\n<li><code>// 2</code>: The <code>decodeAuthHeader</code> function takes the <code>Authorization</code> header and parses it to return the payload of the JWT. </li>\n<li><code>// 3</code>: The <code>Authorization</code> header, contains the <code>type</code> or scheme of authorization followed by the token. In our case, <code>\"Bearer\"</code> represents the authorization scheme. Since the server is only interested in the JWT token itself, you can get rid of the <code>\"Bearer\"</code> and keep only the token. <em>If you’re interested to learn more, here is a nice <a href=\"https://security.stackexchange.com/q/108662\">stackoverflow question</a> explaining this convention</em>. </li>\n<li><code>// 4</code>: The <code>jwt.verify()</code> functions decodes the token. It also needs access to the secret (or a public key) which was used to sign the token. </li>\n</ul>\n<p>There’s one more issue to solve. The GraphQL resolvers don’t have access to the <code>request</code> headers and have no way to get the token from the <code>Authorization</code> header. To solve this, you are going to add the <code>userId</code> received from the decoded JWT to the  GraphQL <code>context</code>, so that every resolver has access to the <code>userId</code>. </p>\n<Instruction>\n<p>Update <code>src/context.ts</code> to add the decoded JWT from the request header:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-typescript\"  path=\".../hackernews-typescript/src/context.ts\"  nocopy=\"false\"><code><span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span> PrismaClient <span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">\"@prisma/client\"</span><span class=\"token punctuation\">;</span>\n<span class=\"gatsby-highlight-code-line\"><span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span> decodeAuthHeader<span class=\"token punctuation\">,</span> AuthTokenPayload <span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">\"./utils/auth\"</span><span class=\"token punctuation\">;</span>   \n</span><span class=\"gatsby-highlight-code-line\"><span class=\"token keyword\">import</span> <span class=\"token punctuation\">{</span> Request <span class=\"token punctuation\">}</span> <span class=\"token keyword\">from</span> <span class=\"token string\">\"express\"</span><span class=\"token punctuation\">;</span>  \n</span>\n<span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> prisma <span class=\"token operator\">=</span> <span class=\"token keyword\">new</span> <span class=\"token class-name\">PrismaClient</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n\n<span class=\"token keyword\">export</span> <span class=\"token keyword\">interface</span> <span class=\"token class-name\">Context</span> <span class=\"token punctuation\">{</span>\n    prisma<span class=\"token punctuation\">:</span> PrismaClient<span class=\"token punctuation\">;</span>\n<span class=\"gatsby-highlight-code-line\">    userId<span class=\"token operator\">?</span><span class=\"token punctuation\">:</span> <span class=\"token keyword\">number</span><span class=\"token punctuation\">;</span>  <span class=\"token comment\" spellcheck=\"true\">// 1</span>\n</span><span class=\"token punctuation\">}</span>\n\n<span class=\"gatsby-highlight-code-line\"><span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> context <span class=\"token operator\">=</span> <span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span> req <span class=\"token punctuation\">}</span><span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span> req<span class=\"token punctuation\">:</span> Request <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">:</span> Context <span class=\"token operator\">=</span><span class=\"token operator\">></span> <span class=\"token punctuation\">{</span>   <span class=\"token comment\" spellcheck=\"true\">// 2</span>\n</span><span class=\"gatsby-highlight-code-line\">    <span class=\"token keyword\">const</span> token <span class=\"token operator\">=</span>\n</span><span class=\"gatsby-highlight-code-line\">        req <span class=\"token operator\">&amp;&amp;</span> req<span class=\"token punctuation\">.</span>headers<span class=\"token punctuation\">.</span>authorization\n</span><span class=\"gatsby-highlight-code-line\">            <span class=\"token operator\">?</span> <span class=\"token function\">decodeAuthHeader</span><span class=\"token punctuation\">(</span>req<span class=\"token punctuation\">.</span>headers<span class=\"token punctuation\">.</span>authorization<span class=\"token punctuation\">)</span>\n</span><span class=\"gatsby-highlight-code-line\">            <span class=\"token punctuation\">:</span> <span class=\"token keyword\">null</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">\n</span><span class=\"gatsby-highlight-code-line\">    <span class=\"token keyword\">return</span> <span class=\"token punctuation\">{</span>  \n</span><span class=\"gatsby-highlight-code-line\">        prisma<span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">        userId<span class=\"token punctuation\">:</span> token<span class=\"token operator\">?</span><span class=\"token punctuation\">.</span>userId<span class=\"token punctuation\">,</span> \n</span><span class=\"gatsby-highlight-code-line\">    <span class=\"token punctuation\">}</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\"><span class=\"token punctuation\">}</span><span class=\"token punctuation\">;</span>\n</span></code></pre>\n      </div>\n</Instruction>\n<p>Let’s understand the changes made to <code>context.ts</code>: </p>\n<ul>\n<li><code>// 1</code>: The context <code>interface</code> is updated to have a <code>userId</code> type. This is optional because no <code>userId</code> will be attached to the <code>context</code> when requests are sent without the <code>Authorization</code> header. </li>\n<li><code>// 2</code>: Instead of being an object, <code>context</code> is now a <em>function</em> which needs to be executed to return the actual object of type <code>Context</code>. Apollo Server is smart enough to recognize this change from object to function and will execute the function (with some arguments, like the HTTP request) to resolve the final context object. </li>\n</ul>\n<h3>Updating the <code>post</code> mutation</h3>\n<p>Now that there is an authentication scheme in place, you are going to make two changes to the <code>post</code> mutation. Firstly you are going to enforce only authenticated users can create a new <code>Link</code> record using the <code>post</code> mutation. Secondly, in your database, you are going to connect the <code>Link</code>  with the <code>User</code> who is posting through the <code>postedBy</code> relation.</p>\n<Instruction>\n<p>Update the implementation of the <code>post</code> resolver in <code>src/graphql/Link.ts</code>:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-typescript\"  path=\"../hackernews-typescript/src/graphql/Link.ts\"  nocopy=\"false\"><code><span class=\"token keyword\">export</span> <span class=\"token keyword\">const</span> LinkMutation <span class=\"token operator\">=</span> <span class=\"token function\">extendType</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span>\n    type<span class=\"token punctuation\">:</span> <span class=\"token string\">\"Mutation\"</span><span class=\"token punctuation\">,</span>\n    <span class=\"token function\">definition</span><span class=\"token punctuation\">(</span>t<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n        t<span class=\"token punctuation\">.</span>nonNull<span class=\"token punctuation\">.</span><span class=\"token function\">field</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"post\"</span><span class=\"token punctuation\">,</span> <span class=\"token punctuation\">{</span>\n            type<span class=\"token punctuation\">:</span> <span class=\"token string\">\"Link\"</span><span class=\"token punctuation\">,</span>\n            args<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span>\n                description<span class=\"token punctuation\">:</span> <span class=\"token function\">nonNull</span><span class=\"token punctuation\">(</span><span class=\"token function\">stringArg</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n                url<span class=\"token punctuation\">:</span> <span class=\"token function\">nonNull</span><span class=\"token punctuation\">(</span><span class=\"token function\">stringArg</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">,</span>\n            <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n            <span class=\"token function\">resolve</span><span class=\"token punctuation\">(</span>parent<span class=\"token punctuation\">,</span> args<span class=\"token punctuation\">,</span> context<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>   \n<span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">const</span> <span class=\"token punctuation\">{</span> description<span class=\"token punctuation\">,</span> url <span class=\"token punctuation\">}</span> <span class=\"token operator\">=</span> args<span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">const</span> <span class=\"token punctuation\">{</span> userId <span class=\"token punctuation\">}</span> <span class=\"token operator\">=</span> context<span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">if</span> <span class=\"token punctuation\">(</span><span class=\"token operator\">!</span>userId<span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>  <span class=\"token comment\" spellcheck=\"true\">// 1</span>\n</span><span class=\"gatsby-highlight-code-line\">                    <span class=\"token keyword\">throw</span> <span class=\"token keyword\">new</span> <span class=\"token class-name\">Error</span><span class=\"token punctuation\">(</span><span class=\"token string\">\"Cannot post without logging in.\"</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token punctuation\">}</span>\n</span><span class=\"gatsby-highlight-code-line\">\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">const</span> newLink <span class=\"token operator\">=</span> context<span class=\"token punctuation\">.</span>prisma<span class=\"token punctuation\">.</span>link<span class=\"token punctuation\">.</span><span class=\"token function\">create</span><span class=\"token punctuation\">(</span><span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">                    data<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span>\n</span><span class=\"gatsby-highlight-code-line\">                        description<span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">                        url<span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">                        postedBy<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span> connect<span class=\"token punctuation\">:</span> <span class=\"token punctuation\">{</span> id<span class=\"token punctuation\">:</span> userId <span class=\"token punctuation\">}</span> <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>  <span class=\"token comment\" spellcheck=\"true\">// 2</span>\n</span><span class=\"gatsby-highlight-code-line\">                    <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</span><span class=\"gatsby-highlight-code-line\">\n</span><span class=\"gatsby-highlight-code-line\">                <span class=\"token keyword\">return</span> newLink<span class=\"token punctuation\">;</span>\n</span>            <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n        <span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n    <span class=\"token punctuation\">}</span><span class=\"token punctuation\">,</span>\n<span class=\"token punctuation\">}</span><span class=\"token punctuation\">)</span><span class=\"token punctuation\">;</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>Let’s go through the two changes: </p>\n<ul>\n<li><code>// 1</code>: If the <code>userId</code> does not exist in <code>context</code>, the resolver raises an error. As a result, only authorized users can add a new <code>link</code>.  </li>\n<li><code>// 2</code>: To connect the <code>User</code> with the <code>Link</code>, you are specifying a value for the <code>postedBy</code> field (which represents this <code>Link</code> to <code>User</code> relation). The <code>connect</code> operator is used by Prisma to specify which <code>user</code> the newly created <code>link</code> should be associated with. </li>\n</ul>\n<p>At long last, the code for this chapter is complete! This was a pretty long chapter, give yourself a congratulations for finishing it. Now it’s time to test the authentication feature. </p>\n<h3>Testing the authentication flow</h3>\n<p>The very first thing you’ll do is test the <code>signup</code> mutation and thereby create a new <code>User</code> in the database.</p>\n<Instruction>\n<p>Go to Apollo Studio Explorer at <a href=\"http://localhost:3000/\">http://localhost:3000/</a>  and send the following mutation to create a new <code>User</code>:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-graphql\"   nocopy=\"false\"><code><span class=\"token keyword\">mutation</span> <span class=\"token punctuation\">{</span>\n  signup<span class=\"token punctuation\">(</span><span class=\"token attr-name\">name</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"Alice\"</span><span class=\"token punctuation\">,</span> <span class=\"token attr-name\">email</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"alice@prisma.io\"</span><span class=\"token punctuation\">,</span> <span class=\"token attr-name\">password</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"graphql\"</span><span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n    token\n    user <span class=\"token punctuation\">{</span>\n      id\n    <span class=\"token punctuation\">}</span>\n  <span class=\"token punctuation\">}</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>You should see a response like this:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-json\"   nocopy=\"true\"><code><span class=\"token punctuation\">{</span>\n  <span class=\"token property\">\"data\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">{</span>\n    <span class=\"token property\">\"signup\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">{</span>\n      <span class=\"token property\">\"token\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOjIsImlhdCI6MTY0MDM0MDY0NX0.MOcEmbbywdYw3GTNxpdLda1XX4WsyWFZls09E_Ot6D0\"</span><span class=\"token punctuation\">,</span>\n      <span class=\"token property\">\"user\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">{</span>\n        <span class=\"token property\">\"id\"</span><span class=\"token operator\">:</span> <span class=\"token number\">1</span>\n      <span class=\"token punctuation\">}</span>\n    <span class=\"token punctuation\">}</span>\n  <span class=\"token punctuation\">}</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n<Instruction>\n<p>From the server’s response, copy the authentication <code>token</code>. Now in the <code>Headers</code> tab in the bottom middle, add a new header. The name of the header will be <code>Authorization</code> and value will be ”<code>Bearer __TOKEN__</code>”, where ”<code>__TOKEN__</code>” should be replaced with <em>your</em> auth token.  Make sure that the header is enabled by clicking the blue tick mark. </p>\n</Instruction>\n<p><img src=\"https://imgur.com/rB5glDx.png\" alt=\"Adding Authorization Header\"></p>\n<p>Now whenever you’re sending a query/mutation, it will carry the JWT token.</p>\n<Instruction>\n<p>With the <code>Authorization</code> header in place, send the following mutation to your GraphQL server:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-graphql\"   nocopy=\"false\"><code><span class=\"token keyword\">mutation</span> <span class=\"token punctuation\">{</span>\n  post<span class=\"token punctuation\">(</span><span class=\"token attr-name\">url</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"nexusjs.org\"</span><span class=\"token punctuation\">,</span> <span class=\"token attr-name\">description</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"Code-First GraphQL schemas for JavaScript/TypeScript\"</span><span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n    id\n    description\n    url\n    postedBy <span class=\"token punctuation\">{</span>\n      id\n      name\n      email\n    <span class=\"token punctuation\">}</span>\n  <span class=\"token punctuation\">}</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>When your server receives this mutation, it invokes the <code>post</code> resolver and therefore validates the provided JWT. Additionally, the new <code>Link</code> that was created is now connected to\nthe <code>User</code> for which you previously sent the <code>signup</code> mutation.</p>\n<Instruction>\n<p>To verify everything worked, you can send the following <code>login</code> mutation, which also requests the links created by the user you created just now. Verify that the <code>links</code> field contains the link you just created:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-graphql\"   nocopy=\"false\"><code><span class=\"token keyword\">mutation</span> <span class=\"token punctuation\">{</span>\n  login<span class=\"token punctuation\">(</span><span class=\"token attr-name\">email</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"alice@prisma.io\"</span><span class=\"token punctuation\">,</span> <span class=\"token attr-name\">password</span><span class=\"token punctuation\">:</span> <span class=\"token string\">\"graphql\"</span><span class=\"token punctuation\">)</span> <span class=\"token punctuation\">{</span>\n    token\n    user <span class=\"token punctuation\">{</span>\n      email\n      links <span class=\"token punctuation\">{</span>\n        url\n        description\n      <span class=\"token punctuation\">}</span>\n    <span class=\"token punctuation\">}</span>\n  <span class=\"token punctuation\">}</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n</Instruction>\n<p>The response will look similar to this:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-json\"   nocopy=\"true\"><code><span class=\"token punctuation\">{</span>\n  <span class=\"token property\">\"data\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">{</span>\n    <span class=\"token property\">\"login\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">{</span>\n      <span class=\"token property\">\"token\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VySWQiOjIsImlhdCI6MTYzNzU3MDM1N30.LlbzC-vHtvb1kGCpRAf_lQS4TWUyTO5VeaDoBt9lzD8\"</span><span class=\"token punctuation\">,</span>\n      <span class=\"token property\">\"user\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">{</span>\n        <span class=\"token property\">\"email\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"alice@prisma.io\"</span><span class=\"token punctuation\">,</span>\n        <span class=\"token property\">\"links\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">[</span>\n          <span class=\"token punctuation\">{</span>\n            <span class=\"token property\">\"url\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"nexusjs.org\"</span><span class=\"token punctuation\">,</span>\n            <span class=\"token property\">\"description\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"Code-First GraphQL schemas for JavaScript/TypeScript\"</span>\n          <span class=\"token punctuation\">}</span>\n        <span class=\"token punctuation\">]</span>\n      <span class=\"token punctuation\">}</span>\n    <span class=\"token punctuation\">}</span>\n  <span class=\"token punctuation\">}</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n<p><img src=\"https://i.imgur.com/c8OkTyM.gif\" alt=\"Testing Auth Workflow\"></p>\n<p>Moreover, if you try to create a new <code>link</code> with the <code>Authorization</code> header absent or disabled, you should get an error like this:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-json\"   nocopy=\"true\"><code><span class=\"token punctuation\">{</span>\n  <span class=\"token property\">\"errors\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">[</span>\n    <span class=\"token punctuation\">{</span>\n      <span class=\"token property\">\"message\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"Cannot post without logging in.\"</span><span class=\"token punctuation\">,</span>\n      <span class=\"token property\">\"locations\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">[</span>\n        <span class=\"token punctuation\">{</span>\n          <span class=\"token property\">\"line\"</span><span class=\"token operator\">:</span> <span class=\"token number\">2</span><span class=\"token punctuation\">,</span>\n          <span class=\"token property\">\"column\"</span><span class=\"token operator\">:</span> <span class=\"token number\">3</span>\n        <span class=\"token punctuation\">}</span>\n      <span class=\"token punctuation\">]</span><span class=\"token punctuation\">,</span>\n      <span class=\"token property\">\"path\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">[</span>\n        <span class=\"token string\">\"post\"</span>\n      <span class=\"token punctuation\">]</span><span class=\"token punctuation\">,</span>\n      <span class=\"token property\">\"extensions\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">{</span>\n        <span class=\"token property\">\"code\"</span><span class=\"token operator\">:</span> <span class=\"token string\">\"INTERNAL_SERVER_ERROR\"</span><span class=\"token punctuation\">,</span>\n        <span class=\"token property\">\"exception\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">{</span>\n          <span class=\"token property\">\"stacktrace\"</span><span class=\"token operator\">:</span> <span class=\"token punctuation\">[</span>\n            <span class=\"token string\">\"Error: Cannot post without logging in.\"</span><span class=\"token punctuation\">,</span>\n            ...  // long stacktrace\n          <span class=\"token punctuation\">]</span>\n        <span class=\"token punctuation\">}</span>\n      <span class=\"token punctuation\">}</span>\n    <span class=\"token punctuation\">}</span>\n  <span class=\"token punctuation\">]</span><span class=\"token punctuation\">,</span>\n  <span class=\"token property\">\"data\"</span><span class=\"token operator\">:</span> <span class=\"token null\">null</span>\n<span class=\"token punctuation\">}</span>\n</code></pre>\n      </div>\n<blockquote>\n<p><strong>Note:</strong> These error messages are a bit long. If you want more readable errors for your end users, check out <a href=\"https://www.apollographql.com/docs/apollo-server/data/errors/#throwing-errors\">this article</a> on throwing errors in the Apollo documentaiton.</p>\n</blockquote>\n<h3>Exploring your data in Prisma Studio</h3>\n<p>Prisma ships with a powerful database GUI where you can interact with your data: <a href=\"https://github.com/prisma/studio\">Prisma Studio</a>.</p>\n<p>Prisma Studio is different from a typical database GUI (such as <a href=\"https://tableplus.com/\">TablePlus</a>) in that it provides a layer of abstraction which allows you to see your data represented as it is\nin your Prisma data model. So not only can you browse, you can <em>explore the relations</em> between different models (tables) as well. You can also filter results as well as create, update and delete data. </p>\n<p>This is one of the several ways that Prisma bridges the gap between how you structure and interact with your data in your application and how it is actually structured and represented in the\nunderlying database. One major benefit of this is that it helps you to build intuition and understanding of these two linked but separate layers over time.</p>\n<p>Let’s run Prisma Studio and see it in action!</p>\n<Instruction>\n<p>Run the following command in your terminal:</p>\n<div class=\"gatsby-highlight\">\n      <pre class=\"language-bash\"  path=\".../hackernews-typescript\"  nocopy=\"false\"><code>npx prisma studio\n</code></pre>\n      </div>\n</Instruction>\n<p>Running the command should open a tab in your browser automatically (running on <code>http://localhost:5555</code>) where you will see the following interface. Notice that you can open multiple tabs and explore both the <code>Link</code> and <code>User</code> model, along with the relation between them.</p>\n<Instruction>\n<p>Explore Prisma studio and try out different things. Explore the relation between <code>Link</code> and <code>User</code> through the data browser. Try creating and deleting records, or modifying existing ones. </p>\n</Instruction>\n<p><img src=\"https://i.imgur.com/tmwOCDd.gif\" alt=\"Explore Data Prisma Studio\"></p>",
			"fields": {
				"slug": "/typescript-apollo/6-authentication/"
			},
			"excerpt": "In this section, you’re going to implement signup and login functionality that allows users to authenticate against your GraphQL server...",
			"frontmatter": {
				"title": "Authentication",
				"videoId": null,
				"videoAuthor": null,
				"parent": "/opt/build/repo/content/backend/typescript-apollo/6-authentication.md absPath of file",
				"question": "Which HTTP header field carries the authentication token?",
				"answers": [
					"Cache-Control",
					"Token",
					"Authorization",
					"Authentication"
				],
				"correctAnswer": 2,
				"description": "Learn best practices for implementing authentication and authorization with TypeScript, Apollo Server & Prisma.",
				"pageTitle": "Implementing Authentication in a GraphQL server with TypeScript"
			}
		},
		"mds": {
			"edges": [
				{
					"node": {
						"frontmatter": {
							"title": "It was great having you!",
							"description": null
						},
						"fields": {
							"slug": "/success/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with Elixir & Absinthe and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-elixir/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Get started with your favorite technology",
							"description": null
						},
						"fields": {
							"slug": "/choose/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with Elixir & Absinthe as well as best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-elixir/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with Absinthe, implement query resolvers in Elixir and test your queries in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-elixir/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with Elixir & Absinthe and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-elixir/4-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with gqlgen."
						},
						"fields": {
							"slug": "/graphql-go/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with Absinthe & Elixir. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-elixir/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "what are queries and implementing a query in gqlgen"
						},
						"fields": {
							"slug": "/graphql-go/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Creating an app with gqlgen"
						},
						"fields": {
							"slug": "/graphql-go/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "what are mutations and implementing a mutation in gqlgen"
						},
						"fields": {
							"slug": "/graphql-go/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Create and Retrieve Links",
							"description": "Enable Users to create Links and see them"
						},
						"fields": {
							"slug": "/graphql-go/5-create-and-retrieve-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Auth Endpoints",
							"description": "Enable Users to register, login and refresh their token"
						},
						"fields": {
							"slug": "/graphql-go/7-auth-endpoints/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with graphql-java and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-java/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "Summary of building a GraphQL server with GO backend"
						},
						"fields": {
							"slug": "/graphql-go/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn best practices for implementing limit-offset pagination in a GraphQL API using query arguments with a Node.js & Express GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/10-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Logged in User",
							"description": "Get logged-in user in gqlgen"
						},
						"fields": {
							"slug": "/graphql-go/8-logged-in-user-object/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Implement authentication in gqlgen with jwt"
						},
						"fields": {
							"slug": "/graphql-go/6-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with graphql-java and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-java/12-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with graphql-java, implement query resolvers in Java and test your queries in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Alternative approaches to schema development",
							"description": "Learn about an alternative approach to developing schemas with graphl-java with graphql-spqr"
						},
						"fields": {
							"slug": "/graphql-java/11-alternative-approaches/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with graphql-java and best practices for defining the GraphQL schema in this tutorial."
						},
						"fields": {
							"slug": "/graphql-java/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Database",
							"description": "Setup mysql db with migrations in golang"
						},
						"fields": {
							"slug": "/graphql-go/4-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with graphql-java & Java. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connectors",
							"description": "Learn how to write connectors in Java to load data from MongoDB with graphql-java and a Java GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/4-connectors/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations",
							"description": "Learn best practices for implementing advanced GraphQL mutations with Java and graphql-java. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-java/6-more-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing email-password authentication on a GraphQL Server written in Java."
						},
						"fields": {
							"slug": "/graphql-java/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with apollo-server, Node.js & Prisma and best practices for authentication, filtering, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-js/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with Node.js & Express as well as best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-js/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Learn best practices for validation of input arguments and implementing error handling in GraphQL with Java, Node.js & Express."
						},
						"fields": {
							"slug": "/graphql-java/7-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Query",
							"description": "Learn how to define a GraphQL schema, implement query resolvers with Node.js and test your queries in a GraphQL Playground."
						},
						"fields": {
							"slug": "/graphql-js/2-a-simple-query/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Mutation",
							"description": "Learn best practices for implementing GraphQL mutations with graphql-js, Node.js & Prisma. Test your implementation in a GraphQL Playground."
						},
						"fields": {
							"slug": "/graphql-js/3-a-simple-mutation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Subscriptions",
							"description": "Learn how to implement server-side GraphQL subscriptions with Java to add realtime functionality to an app."
						},
						"fields": {
							"slug": "/graphql-java/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Adding a Database",
							"description": "Learn how to add a database to your GraphQL server. The database is powered by Prisma and connected to the server via GraphQL bindings."
						},
						"fields": {
							"slug": "/graphql-js/4-adding-a-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connecting The Server and Database with Prisma Client",
							"description": "Learn how to add a database to your GraphQL server. The database is accessed using Prisma Client."
						},
						"fields": {
							"slug": "/graphql-js/5-connecting-server-and-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Learn best practices for implementing filters in a GraphQL API using query arguments with a Java GraphQL server."
						},
						"fields": {
							"slug": "/graphql-java/9-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Setting up a Django Graphene Project"
						},
						"fields": {
							"slug": "/graphql-python/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing authentication and authorization with Node.js, Express & Prisma."
						},
						"fields": {
							"slug": "/graphql-js/6-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime GraphQL Subscriptions",
							"description": "Learn how to implement GraphQL subscriptions with Node.js, Express & Prisma to add realtime functionality to an app."
						},
						"fields": {
							"slug": "/graphql-js/7-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Building a Server with GraphQL and Graphene"
						},
						"fields": {
							"slug": "/graphql-python/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "Tutorial Summary"
						},
						"fields": {
							"slug": "/graphql-python/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with Node.js and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-js/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Creating Your First Queries"
						},
						"fields": {
							"slug": "/graphql-python/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Creating Data With Mutations"
						},
						"fields": {
							"slug": "/graphql-python/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Authenticating on GraphQL"
						},
						"fields": {
							"slug": "/graphql-python/4-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Handling Errors on GraphQL"
						},
						"fields": {
							"slug": "/graphql-python/6-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Filtering GraphQL Queries"
						},
						"fields": {
							"slug": "/graphql-python/7-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Simple Pagination with Graphene"
						},
						"fields": {
							"slug": "/graphql-python/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering, Pagination & Sorting",
							"description": "Learn how to add filtering and pagination capabilities to a GraphQL API with Node.js, Express & Prisma."
						},
						"fields": {
							"slug": "/graphql-js/8-filtering-pagination-and-sorting/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Links and Voting",
							"description": "Enable Users to create Links and to Vote on them"
						},
						"fields": {
							"slug": "/graphql-python/5-links-and-voting/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with graphql-ruby and best practices for filters, authentication, pagination and subscriptions. Compatible with Apollo."
						},
						"fields": {
							"slug": "/graphql-ruby/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with graphql-ruby and best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/graphql-ruby/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Relay",
							"description": "Using Relay on Graphene"
						},
						"fields": {
							"slug": "/graphql-python/9-relay/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "Learn best practices for implementing GraphQL mutations with Ruby and graphql-ruby. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-ruby/3-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries",
							"description": "Learn how to define the GraphQL schema with graphql-ruby, implement query resolvers in Ruby and use a GraphiQL Playground to test your queries."
						},
						"fields": {
							"slug": "/graphql-ruby/2-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connecting Nodes",
							"description": "Learn best practices for implementing advanced GraphQL mutations with Ruby and graphql-ruby. You can test your implementation in a GraphiQL Playground."
						},
						"fields": {
							"slug": "/graphql-ruby/5-connecting-nodes/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Error Handling",
							"description": "Learn best practices for validation of input arguments and implementing error handling in GraphQL with Ruby."
						},
						"fields": {
							"slug": "/graphql-ruby/6-error-handling/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering",
							"description": "Learn best practices for implementing filters in a GraphQL API using query arguments with a Ruby GraphQL server."
						},
						"fields": {
							"slug": "/graphql-ruby/7-filtering/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with Scala & Sangria and the best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/graphql-scala/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing email-password authentication on a GraphQL Server written in Ruby."
						},
						"fields": {
							"slug": "/graphql-ruby/4-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "In this chapter will be described how to setup the HTTP server, install all dependencies and setup the database."
						},
						"fields": {
							"slug": "/graphql-scala/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Handling Arguments",
							"description": "In this chapter you will learn how to use arguments and how to handle them and pass to the business logic."
						},
						"fields": {
							"slug": "/graphql-scala/3-handling-arguments/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "The first query",
							"description": "In this chapter you will learn last steps of preparation, add graphiql console for easy debugging and at the end, run your first query."
						},
						"fields": {
							"slug": "/graphql-scala/2-preparing-first-query/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deferred Resolvers",
							"description": "In this chapter you will learn how to improve performance with deferred resolvers."
						},
						"fields": {
							"slug": "/graphql-scala/4-deferred-resolvers/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Custom Scalars",
							"description": "In this chapter Link model will get an additional field to store date and time. You will learn how to handle such custom types and use scalars."
						},
						"fields": {
							"slug": "/graphql-scala/5-custom-scalars/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Interfaces",
							"description": "In this chapter we will introduce two additional models. We show the most common parts and extract those as an interface. This time you will work alone."
						},
						"fields": {
							"slug": "/graphql-scala/6-interfaces/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn best practices for implementing limit-offset pagination in a GraphQL API using query arguments with Ruby GraphQL server."
						},
						"fields": {
							"slug": "/graphql-ruby/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations",
							"description": "In this chapter you will learn how to add data to the database."
						},
						"fields": {
							"slug": "/graphql-scala/8-mutations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with graphql-ruby & Ruby and best practices for filters, authentication, pagination and subscriptions."
						},
						"fields": {
							"slug": "/graphql-ruby/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Sangria's Middleware allows pre-processing queries. Gather all useful data in the following subqueries."
						},
						"fields": {
							"slug": "/graphql-scala/9-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with Apollo, TypeScript, Nexus & Prisma and best practices for authentication, filtering and pagination."
						},
						"fields": {
							"slug": "/typescript-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Relations",
							"description": "In this chapter you will learn how to connect models with relations. You will also learn how to prepare fetchers to use either relations or ids to get entities."
						},
						"fields": {
							"slug": "/graphql-scala/7-relations/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Mutation",
							"description": "Learn best practices for implementing GraphQL mutations with Nexus and TypeScript. Test your implementation in Apollo Studio Explorer."
						},
						"fields": {
							"slug": "/typescript-apollo/3-a-simple-mutation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with TypeScript and best practices for filters, authentication and pagination."
						},
						"fields": {
							"slug": "/typescript-apollo/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connecting The Server and Database with Prisma Client",
							"description": "Learn how to add a database to your GraphQL server. The database is accessed using Prisma Client."
						},
						"fields": {
							"slug": "/typescript-apollo/5-connecting-server-and-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how to setup a GraphQL server with TypeScript & Apollo as well as best practices for defining the GraphQL schema."
						},
						"fields": {
							"slug": "/typescript-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Adding a Database",
							"description": "Learn how to add a database to your GraphQL server. The database is powered by Prisma."
						},
						"fields": {
							"slug": "/typescript-apollo/4-adding-a-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing authentication and authorization with TypeScript, Apollo Server & Prisma."
						},
						"fields": {
							"slug": "/typescript-apollo/6-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deploying to Production",
							"description": "Inside ."
						},
						"fields": {
							"slug": "/typescript-apollo/9-deployment/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a GraphQL server with Node.js, Fastify, TypeScript, GraphQL-Helix & Prisma 3 and best practices for authentication, filtering, pagination and subscriptions."
						},
						"fields": {
							"slug": "/typescript-helix/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Project Setup",
							"description": "Learn how to setup a basic NodeJS & TypeScript project"
						},
						"fields": {
							"slug": "/typescript-helix/1-project-setup/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "You learned how to build a GraphQL server with Node.js and best practices for filters, authentication and pagination"
						},
						"fields": {
							"slug": "/typescript-helix/11-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering, Pagination & Sorting",
							"description": "Learn how to add filtering and pagination capabilities to a GraphQL API with Node.js, Fastify & Prisma."
						},
						"fields": {
							"slug": "/typescript-helix/10-filtering-pagination-and-sorting/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Voting and Custom GraphQL Scalars",
							"description": "Learn how to implement a voting feature and use custom scalars for a Hackernews Clone with Apollo Server, Nexus and Prisma."
						},
						"fields": {
							"slug": "/typescript-apollo/7-voting-and-scalars/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering, Pagination & Sorting",
							"description": "Learn how to add filtering and pagination capabilities to a GraphQL API with TypeScript, Apollo Server & Prisma."
						},
						"fields": {
							"slug": "/typescript-apollo/8-filtering-pagination-and-sorting/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Creating a GraphQL Server",
							"description": "Getting Started with a GraphQL server based on Fastify and GraphQL-Helix"
						},
						"fields": {
							"slug": "/typescript-helix/3-graphql-server/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Query",
							"description": "Learn how to define a GraphQL schema with Nexus, implement query resolvers and test your queries in Apollo Studio Explorer"
						},
						"fields": {
							"slug": "/typescript-apollo/2-a-simple-query/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Learn how write a GraphQL schema and how to try and run it."
						},
						"fields": {
							"slug": "/typescript-helix/2-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Query",
							"description": "Learn how to define a GraphQL schema, implement query resolvers with Node.js and test your queries in a GraphiQL."
						},
						"fields": {
							"slug": "/typescript-helix/4-a-simple-query/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "A Simple Mutation",
							"description": "Learn best practices for implementing GraphQL mutations with graphql-js, Node.js & Prisma. Test your implementation in a GraphiQL."
						},
						"fields": {
							"slug": "/typescript-helix/5-a-simple-mutation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, Angular & Apollo Client. You'll use angular-cli for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/angular-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Connecting The Server and Database with Prisma Client",
							"description": "Learn how to add a database to your GraphQL server. The database is accessed using Prisma Client."
						},
						"fields": {
							"slug": "/typescript-helix/7-connecting-server-and-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with angular-cli and the backend with Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with Angular & Apollo Client you learned how to build a production-ready Hackernews clone using angular-cli & Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/11-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Adding a Database",
							"description": "Learn how to add a database to your GraphQL server. The database is powered by Prisma and connected to the server via GraphQL bindings."
						},
						"fields": {
							"slug": "/typescript-helix/6-adding-a-database/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices for implementing authentication and authorization with Node.js, TypeScript, Fastify & Prisma."
						},
						"fields": {
							"slug": "/typescript-helix/8-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deploying",
							"description": "Learn how to deploy an application built with Angular and Graphcool through Surge.sh."
						},
						"fields": {
							"slug": "/angular-apollo/10-deploying/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading link-items",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your Angular components."
						},
						"fields": {
							"slug": "/angular-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use Apollo's service injected in the component to define and send mutations."
						},
						"fields": {
							"slug": "/angular-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your Angular components."
						},
						"fields": {
							"slug": "/angular-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime GraphQL Subscriptions",
							"description": "Learn how to implement GraphQL subscriptions with Node.js, Express & Prisma to add realtime functionality to an app."
						},
						"fields": {
							"slug": "/typescript-helix/9-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in an Angular app with Graphcool."
						},
						"fields": {
							"slug": "/angular-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, Ember, and Apollo Client. You'll use Ember and ember-apollo-client for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/ember-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/angular-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & Angular. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/angular-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use Angular Router  together with GraphQL and Apollo Client to implement navigation in an Angular app."
						},
						"fields": {
							"slug": "/angular-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a Angular app. The Graphcool API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/angular-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with Ember & Apollo Client you learned how to build a production-ready Hackernews clone using ember-apollo-client & Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/8-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/ember-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with ember-cli, ember-apollo-client, and the backend with Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Navigation",
							"description": "Learn how to use Ember components together with GraphQL and Apollo Client to implement navigation in an Ember app."
						},
						"fields": {
							"slug": "/ember-apollo/4-navigation/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your Ember app."
						},
						"fields": {
							"slug": "/ember-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with React and Apollo Client we learned how to build a production-ready Hackernews clone using create-react-app & Prisma."
						},
						"fields": {
							"slug": "/react-apollo/10-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/ember-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, React and Apollo Client. Use create-react-app for the frontend and Apollo Server with Prisma for the backend."
						},
						"fields": {
							"slug": "/react-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/react-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how to use GraphQL mutations with Apollo Client. Use Apollo's `<Mutation />` component to define and send mutations."
						},
						"fields": {
							"slug": "/react-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with create-react-app and the backend with Prisma."
						},
						"fields": {
							"slug": "/react-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use React Router 5 together with GraphQL and Apollo Client to implement navigation in a React app. Each route will be represented as a `Link`."
						},
						"fields": {
							"slug": "/react-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a production-ready Hackernews clone with GraphQL, React & Relay. You'll use create-react-app for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/react-relay/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in an Ember app with Graphcool."
						},
						"fields": {
							"slug": "/ember-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL and Apollo Client to provide an email/password login in a React app with Prisma."
						},
						"fields": {
							"slug": "/react-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use `ember-apollo-client` to send mutations."
						},
						"fields": {
							"slug": "/ember-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Prisma provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/react-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a React app. The Prisma API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/react-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically be reflected in our React components."
						},
						"fields": {
							"slug": "/react-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with create-react-app and the backend with Graphcool."
						},
						"fields": {
							"slug": "/react-relay/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL Fragments and Relay's QueryRenderer to load data from a server and display it in your React components."
						},
						"fields": {
							"slug": "/react-relay/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how to use Relay Modern's new Mutation API to send mutations to a GraphQL server. With Relay Modern you can also update the cache directly after the mutation."
						},
						"fields": {
							"slug": "/react-relay/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": null
						},
						"fields": {
							"slug": "/react-relay/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & React. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/react-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with React & Relay Modern you learned how to build a production-ready Hackernews clone using create-react-app & Graphcool."
						},
						"fields": {
							"slug": "/react-relay/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Relay Modern's imperative API to update the cache after a GraphQL mutation. The updates will automatically end up in your React components."
						},
						"fields": {
							"slug": "/react-relay/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Relay to provide an email-and-password-based login in a React app with Graphcool."
						},
						"fields": {
							"slug": "/react-relay/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, React & urql. Use create-react-app for the frontend and graphql-yoga & Prisma for the backend."
						},
						"fields": {
							"slug": "/react-urql/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with urql to load data from a GraphQL API and display it in your React components."
						},
						"fields": {
							"slug": "/react-urql/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement cursor-based pagination with GraphQL and Relay Modern using `PaginationContainer`. Relay uses the concept of connections to represent lists."
						},
						"fields": {
							"slug": "/react-relay/8-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Relay Modern & React. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/react-relay/7-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with create-react-app and the backend with Prisma."
						},
						"fields": {
							"slug": "/react-urql/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with urql. Use urql's 'useMutation' hook to define and send mutations."
						},
						"fields": {
							"slug": "/react-urql/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn how to implement authentication with GraphQL & urql to provide an email-&-password login in a React app with Prisma."
						},
						"fields": {
							"slug": "/react-urql/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination and Cache Updates",
							"description": "Learn how to implement limit-offset pagination and cache updates with GraphQL and urql in a React app and update."
						},
						"fields": {
							"slug": "/react-urql/7-pagination-and-cache-updates/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Queries",
							"description": "Learn more about mutations and queries with React & urql.."
						},
						"fields": {
							"slug": "/react-urql/6-more-mutations-and-queries/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with React & urql you learned how to build a production-ready Hackernews clone using create-react-app & Prisma."
						},
						"fields": {
							"slug": "/react-urql/9-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with urql & React. The websockets transport will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/react-urql/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn how to build a Hackernews clone with GraphQL, VueJS & Apollo Client. You'll use vue-cli for the frontend and Graphcool for the backend."
						},
						"fields": {
							"slug": "/vue-apollo/0-introduction/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use react-router 5 together with GraphQL and urql to implement navigation in a React app. Each route will be represented as a Link."
						},
						"fields": {
							"slug": "/react-urql/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Getting Started",
							"description": "Start building a Hackernews clone. Create the frontend with vue-cli and the backend with Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/1-getting-started/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Summary",
							"description": "In this Fullstack GraphQL Tutorial with VueJS & Apollo Client you learned how to build a production-ready Hackernews clone using vue-cli & Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/11-summary/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Queries: Loading Links",
							"description": "Learn how you can use GraphQL queries with Apollo Client to load data from a server and display it in your VueJS components."
						},
						"fields": {
							"slug": "/vue-apollo/2-queries-loading-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Deploying",
							"description": "Learn how to deploy an application built with VueJS and Graphcool through Surge.sh."
						},
						"fields": {
							"slug": "/vue-apollo/10-deploying/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Routing",
							"description": "Learn how to use vue-router together with GraphQL and Apollo Client to implement navigation in a VueJS app."
						},
						"fields": {
							"slug": "/vue-apollo/4-routing/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Authentication",
							"description": "Learn best practices to implement authentication with GraphQL & Apollo Client to provide an email-and-password-based login in a VueJS app with Graphcool."
						},
						"fields": {
							"slug": "/vue-apollo/5-authentication/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Realtime Updates with GraphQL Subscriptions",
							"description": "Learn how to implement realtime functionality using GraphQL subscriptions with Apollo Client & VueJS. The websockets will be handled by subscriptions-transport-ws."
						},
						"fields": {
							"slug": "/vue-apollo/8-subscriptions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Filtering: Searching the List of Links",
							"description": "Learn how to use filters with GraphQL and Apollo Client. Graphcool provides a powerful filter and ordering API that you'll explore in this example."
						},
						"fields": {
							"slug": "/vue-apollo/7-filtering-searching-the-list-of-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More Mutations and Updating the Store",
							"description": "Learn how to use Apollo's imperative store API to update the cache after a GraphQL mutation. The updates will automatically end up in your VueJS components."
						},
						"fields": {
							"slug": "/vue-apollo/6-more-mutations-and-updating-the-store/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Pagination",
							"description": "Learn how to implement limit-offset pagination with GraphQL and Apollo Client in a VueJS app. The Graphcool API exposes the required arguments for lists."
						},
						"fields": {
							"slug": "/vue-apollo/9-pagination/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Mutations: Creating Links",
							"description": "Learn how you can use GraphQL mutations with Apollo Client. Use Apollo's `mutate` method to define and send mutations."
						},
						"fields": {
							"slug": "/vue-apollo/3-mutations-creating-links/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Clients",
							"description": "Learn about GraphQL clients, like Relay and Apollo Client, and the features they provide, such as caching, schema validation and query/view colocation."
						},
						"fields": {
							"slug": "/advanced/0-clients/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Server",
							"description": "Learn about the major concepts of GraphQL servers, how it executes GraphQL queries with resolver functions and batches multiple requests."
						},
						"fields": {
							"slug": "/advanced/1-server/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Tooling and Ecosystem",
							"description": "Learn about the GraphQL ecosystem and its awesome tooling like the GraphiQL playgrounds or self-generating docs through introspection."
						},
						"fields": {
							"slug": "/advanced/3-tooling-and-ecosystem/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "More GraphQL Concepts",
							"description": "Learn about advanced concepts of the GraphQL language like fragments, query arguments, aliases, interfaces and more SDL features"
						},
						"fields": {
							"slug": "/advanced/2-more-graphql-concepts/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Common Questions",
							"description": "Answer common GraphQL questions about server-side caching, authentication, authorization, error handling, offline usage and more."
						},
						"fields": {
							"slug": "/advanced/5-common-questions/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "GraphQL is the better REST",
							"description": "Learn why GraphQL is a more efficient and flexible alternative to REST APIs. It has a strong type system and avoids frontend issues like over- and underfetching."
						},
						"fields": {
							"slug": "/basics/1-graphql-is-the-better-rest/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Security",
							"description": "Learn about different security aspects and strategies for GraphQL, such as timeouts, maximum query depth, query complexity and throttling."
						},
						"fields": {
							"slug": "/advanced/4-security/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Big Picture (Architecture)",
							"description": "Learn about different architectural use cases of GraphQL and the major components on the backend and the frontend, like resolver functions and client libraries."
						},
						"fields": {
							"slug": "/basics/3-big-picture/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Core Concepts",
							"description": "Learn about the basic GraphQL language concepts, such as Queries, Mutations, Subscriptions and the GraphQL Schema & SDL."
						},
						"fields": {
							"slug": "/basics/2-core-concepts/"
						}
					}
				},
				{
					"node": {
						"frontmatter": {
							"title": "Introduction",
							"description": "Learn all about GraphQL and why it is an API technology that's superior to REST. It is not only for React & Javascript developers but can be used for any API."
						},
						"fields": {
							"slug": "/basics/0-introduction/"
						}
					}
				}
			]
		}
	},
	"pathContext": {
		"slug": "/typescript-apollo/6-authentication/"
	}
};


//////////////////
// WEBPACK FOOTER
// ./~/json-loader!./.cache/json/typescript-apollo-6-authentication.json
// module id = ./node_modules/json-loader/index.js!./.cache/json/typescript-apollo-6-authentication.json
// module chunks = 30